import { defineCrestModule } from '@crest/platform-contracts/modules';
import { projectsProjectReferenceCapability } from '@crest/module-projects/contracts/project-reference';
import { platformOptionalActionProviderHostCapability, platformOptionalFactProviderHostCapability, platformPortalAccessScopeProviderHostCapability, platformPortalHostCapability, } from '@crest/platform-contracts/hosts';
import { documentsAuthenticityCapability, documentsInstancesCapability, documentsSignaturesCapability, } from './contracts/documents-instance.contract.js';
import { documentsTemplatePreviewCapability, documentsTemplatesCapability, } from './contracts/documents-template.contract.js';
import { documentsInsuranceClaimsReadinessProviderId, documentsSignedAuthorizationsCapability, documentsSignedCompletionsCapability, } from './contracts/documents-insurance-claims-readiness.contract.js';
import { documentsInsuranceClaimsCompletionCapability, documentsInsuranceClaimsCompletionProviderId, } from './contracts/documents-insurance-claims-completion.contract.js';
import { documentsInsuranceClaimsNoticeArtifactCapability, documentsInsuranceClaimsNoticeArtifactProviderId, } from './contracts/documents-insurance-claims-notice-artifact.contract.js';
import { documentsInsuranceClaimsScopeCompletionCapability, documentsInsuranceClaimsScopeCompletionProviderId, } from './contracts/documents-insurance-claims-scope-completion.contract.js';
import { documentsTrainingRecordArtifactsCapability, documentsTrainingRecordFactsProviderCapability, } from './contracts/documents-training-record-artifact.contract.js';
import { documentsKnowledgeSignedPolicyAcknowledgementCapability, documentsKnowledgeSignedPolicyArtifactsCapability, documentsKnowledgeSignedPolicyFactsCapability, } from './contracts/documents-knowledge-signed-policy.contract.js';
import { documentsWorkforceProtectedFormEligibilityCapability, documentsWorkforceProtectedFormsCapability, documentsWorkforceProtectedFormSubmissionCapability, } from './contracts/documents-workforce-protected-form.contract.js';
import { documentsProtectedObjectUploadsCapability } from './contracts/documents-protected-object-upload.contract.js';
import { documentsKnowledgeSignedPolicyCopyNotificationEventName, documentsNotificationRequestsCapability, documentsWorkforceProtectedFormNotificationEventName, } from './contracts/documents-notification-request.contract.js';
import { documentsPortalEnvelopeCapability, documentsPortalEnvelopeActionGrant, documentsPortalEnvelopeReadGrant, documentsPortalEnvelopeScopeProviderId, } from './contracts/documents-portal-envelope.contract.js';
import { documentsEstimatingAcceptanceEnvelopesCapability, documentsEstimatingProposalAcceptanceFactsCapability, } from './contracts/documents-estimating-acceptance-envelope.contract.js';
import { documentsAssistantContextCapability, documentsAssistantContextProviderId, } from './contracts/documents-assistant-context.contract.js';
import { documentsLegacyImportCapability } from './import/documents-legacy-import.contracts.js';
export const documentsModuleManifest = defineCrestModule({
    id: 'documents',
    version: '1.0.0',
    kind: 'business',
    required: false,
    requires: ['platform', 'projects'],
    requiredCapabilities: [
        'platform.tenant-context.v1',
        'platform.module-access.v1',
        'platform.membership-directory.v1',
        'platform.storage-object-reference.v1',
        platformOptionalFactProviderHostCapability,
        platformOptionalActionProviderHostCapability,
        platformPortalAccessScopeProviderHostCapability,
        platformPortalHostCapability,
        projectsProjectReferenceCapability,
        'platform.import-resolution.v1',
        'projects.import-resolution.v1',
        'relationships.import-resolution.v1',
    ],
    optionalCapabilities: [
        documentsTrainingRecordFactsProviderCapability,
        documentsKnowledgeSignedPolicyFactsCapability,
        documentsKnowledgeSignedPolicyAcknowledgementCapability,
        documentsWorkforceProtectedFormEligibilityCapability,
        documentsWorkforceProtectedFormSubmissionCapability,
        documentsEstimatingProposalAcceptanceFactsCapability,
    ],
    optionalCapabilityFallbacks: [{
            capability: documentsTrainingRecordFactsProviderCapability,
            degradedMode: 'documents.degraded.retained-training-records-only.v1',
        }, {
            capability: documentsKnowledgeSignedPolicyFactsCapability,
            degradedMode: 'documents.degraded.retained-signed-policy-artifacts-only.v1',
        }, {
            capability: documentsKnowledgeSignedPolicyAcknowledgementCapability,
            degradedMode: 'documents.degraded.signed-policy-creation-unavailable.v1',
        }, {
            capability: documentsWorkforceProtectedFormEligibilityCapability,
            degradedMode: 'documents.degraded.retained-workforce-protected-forms-only.v1',
        }, {
            capability: documentsWorkforceProtectedFormSubmissionCapability,
            degradedMode: 'documents.degraded.workforce-protected-form-creation-unavailable.v1',
        }, {
            capability: documentsEstimatingProposalAcceptanceFactsCapability,
            degradedMode: 'documents.degraded.estimating-acceptance-envelope-unavailable.v1',
        }],
    provides: [
        documentsTemplatesCapability,
        documentsTemplatePreviewCapability,
        documentsInstancesCapability,
        documentsSignaturesCapability,
        documentsAuthenticityCapability,
        documentsSignedAuthorizationsCapability,
        documentsSignedCompletionsCapability,
        documentsInsuranceClaimsCompletionCapability,
        documentsInsuranceClaimsNoticeArtifactCapability,
        documentsInsuranceClaimsScopeCompletionCapability,
        documentsTrainingRecordArtifactsCapability,
        documentsKnowledgeSignedPolicyArtifactsCapability,
        documentsWorkforceProtectedFormsCapability,
        documentsProtectedObjectUploadsCapability,
        documentsNotificationRequestsCapability,
        documentsPortalEnvelopeCapability,
        documentsEstimatingAcceptanceEnvelopesCapability,
        documentsAssistantContextCapability,
        documentsLegacyImportCapability,
    ],
    permissions: [
        'documents.templates.read',
        'documents.templates.manage',
        'documents.templates.preview',
        'documents.templates.publish',
        'documents.instances.read',
        'documents.instances.manage',
        'documents.instances.send',
        'documents.instances.lifecycle',
        'documents.signatures.read',
        'documents.signatures.record',
        'documents.authenticity.read',
        'documents.artifacts.finalize',
        'documents.training-records.read',
        'documents.training-records.create',
        'documents.knowledge-signed-policies.read',
        'documents.knowledge-signed-policies.sign',
        'documents.knowledge-signed-policies.finalize',
        'documents.workforce-protected-forms.assets.read',
        'documents.workforce-protected-forms.assets.manage',
        'documents.workforce-protected-forms.read-own',
        'documents.workforce-protected-forms.read-all',
        'documents.workforce-protected-forms.submit-own',
        'documents.workforce-protected-forms.submit-all',
        documentsPortalEnvelopeReadGrant,
        documentsPortalEnvelopeActionGrant,
        'documents.import.execute',
    ],
    settings: [
        'documents.templates.configuration',
        'documents.templates.display',
        'documents.copy.templates',
        'documents.instances.configuration',
        'documents.instances.display',
        'documents.copy.instances',
        'documents.portal-envelopes.configuration',
        'documents.portal-envelopes.display',
        'documents.copy.portal-envelopes',
        'documents.training-records.configuration',
        'documents.training-records.display',
        'documents.copy.training-records',
        'documents.protected-forms.display',
        'documents.copy.protected-forms',
        'documents.signed-policies.display',
        'documents.copy.signed-policies',
    ],
    copyCatalogs: [
        'documents.copy.templates',
        'documents.copy.instances',
        'documents.copy.portal-envelopes',
        'documents.copy.training-records',
        'documents.copy.protected-forms',
        'documents.copy.signed-policies',
    ],
    events: {
        publishes: [
            'documents.template-created.v1',
            'documents.template-version-created.v1',
            'documents.template-published.v1',
            'documents.template-archived.v1',
            'documents.template-restored.v1',
            'documents.template-rolled-back.v1',
            'documents.instance-created.v1',
            'documents.instance-sent.v1',
            'documents.instance-viewed.v1',
            'documents.delivery-intent-prepared.v1',
            'documents.instance-voided.v1',
            'documents.instance-declined.v1',
            'documents.instance-expired.v1',
            'documents.instance-signed.v1',
            'documents.instance-artifact-finalized.v1',
            'documents.training-record-artifact-created.v1',
            'documents.knowledge-signed-policy-signed.v1',
            'documents.knowledge-signed-policy-finalized.v1',
            'documents.workforce-form-asset-create.v1',
            'documents.workforce-form-asset-append-version.v1',
            'documents.workforce-form-asset-publish.v1',
            'documents.workforce-form-asset-archive.v1',
            'documents.workforce-form-asset-restore.v1',
            'documents.workforce-form-asset-rollback.v1',
            'documents.workforce-protected-form-submitted.v1',
            documentsWorkforceProtectedFormNotificationEventName,
            documentsKnowledgeSignedPolicyCopyNotificationEventName,
        ],
        subscribes: [],
    },
    server: {
        routes: [
            { id: 'documents.routes.instances', priority: 0 },
            { id: 'documents.routes.templates', priority: 10 },
            {
                id: 'documents.routes.training-records',
                requiredPermission: 'documents.training-records.read',
                priority: 20,
            },
            {
                id: 'documents.routes.knowledge-signed-policies',
                requiredPermission: 'documents.knowledge-signed-policies.read',
                priority: 30,
            },
            {
                id: 'documents.routes.protected-object-uploads',
                priority: 35,
            },
            {
                id: 'documents.routes.workforce-form-assets',
                priority: 40,
            },
            {
                id: 'documents.routes.workforce-protected-form-submissions',
                priority: 50,
            },
        ],
        jobs: [],
        subscribers: [],
        slots: [{
                id: documentsPortalEnvelopeScopeProviderId,
                requiredCapability: platformPortalAccessScopeProviderHostCapability,
                requiredPermission: 'documents.instances.read',
                priority: 10,
            }, {
                id: documentsInsuranceClaimsReadinessProviderId,
                requiredCapability: platformOptionalFactProviderHostCapability,
                requiredPermission: 'documents.instances.read',
                priority: 0,
            }, {
                id: documentsInsuranceClaimsCompletionProviderId,
                requiredCapability: platformOptionalFactProviderHostCapability,
                requiredPermission: 'documents.instances.read',
                priority: 10,
            }, {
                id: documentsInsuranceClaimsNoticeArtifactProviderId,
                requiredCapability: platformOptionalFactProviderHostCapability,
                requiredPermission: 'documents.instances.read',
                priority: 20,
            }, {
                id: documentsInsuranceClaimsScopeCompletionProviderId,
                requiredCapability: platformOptionalFactProviderHostCapability,
                requiredPermission: 'documents.instances.read',
                priority: 30,
            }, {
                id: documentsAssistantContextProviderId,
                requiredCapability: platformOptionalFactProviderHostCapability,
                priority: 40,
            }],
    },
    web: {
        routes: [
            { id: 'documents.web.instances', priority: 0 },
            { id: 'documents.web.templates', priority: 10 },
            {
                id: 'documents.web.training-records',
                requiredPermission: 'documents.training-records.read',
                priority: 20,
            },
            {
                id: 'documents.web.protected-forms',
                requiredPermission: 'documents.workforce-protected-forms.read-own',
                priority: 30,
            },
            {
                id: 'documents.web.signed-policies',
                requiredPermission: 'documents.knowledge-signed-policies.read',
                priority: 40,
            },
        ],
        navigation: [
            { id: 'documents.navigation.instances', priority: 80 },
            { id: 'documents.navigation.templates', priority: 81 },
            {
                id: 'documents.navigation.training-records',
                requiredPermission: 'documents.training-records.read',
                priority: 82,
            },
            {
                id: 'documents.navigation.protected-forms',
                requiredPermission: 'documents.workforce-protected-forms.read-own',
                priority: 83,
            },
            {
                id: 'documents.navigation.signed-policies',
                requiredPermission: 'documents.knowledge-signed-policies.read',
                priority: 84,
            },
        ],
        slots: [],
    },
    mobile: { routes: [], slots: [] },
    portal: {
        routes: [],
        slots: [{
                id: 'documents.portal.project-envelopes',
                requiredCapability: platformPortalHostCapability,
                requiredPermission: documentsPortalEnvelopeReadGrant,
                priority: 30,
            }],
    },
    database: {
        schema: 'documents',
        owns: [
            'documents.templates',
            'documents.template_versions',
            'documents.template_lifecycle_events',
            'documents.template_commands',
            'documents.execute_template_command',
            'documents.document_instances',
            'documents.document_signatures',
            'documents.document_events',
            'documents.document_commands',
            'documents.execute_document_command',
            'documents.read_portal_envelopes',
            'documents.execute_portal_envelope_command',
            'documents.estimating_acceptance_envelope_bindings',
            'documents.bind_estimating_acceptance_envelope',
            'documents.verify_portal_estimating_acceptance',
            'documents.training_record_artifacts',
            'documents.knowledge_signed_policy_artifacts',
            'documents.knowledge_signed_policy_finalizations',
            'documents.knowledge_signed_policy_commands',
            'documents.knowledge_signed_policy_events',
            'documents.create_knowledge_signed_policy_artifact_v1',
            'documents.finalize_knowledge_signed_policy_artifact_v1',
            'documents.workforce_form_assets',
            'documents.workforce_form_asset_versions',
            'documents.workforce_form_asset_commands',
            'documents.workforce_protected_form_submissions',
            'documents.workforce_protected_form_submission_commands',
            'documents.list_workforce_form_assets',
            'documents.get_workforce_form_asset',
            'documents.list_workforce_form_asset_versions',
            'documents.execute_workforce_form_asset_command',
            'documents.list_workforce_protected_form_submissions',
            'documents.get_workforce_protected_form_submission',
            'documents.get_workforce_protected_form_submission_by_command',
            'documents.create_workforce_protected_form_submission_v1',
            'documents.legacy_import_runs',
            'documents.legacy_import_identity_map',
            'documents.legacy_import_receipts',
            'documents.legacy_import_changes',
            'documents.legacy_import_rejects',
            'documents.legacy_import_rollbacks',
            'documents.legacy_import_event_evidence',
            'documents.legacy_import_reject_mutation_v1',
            'documents.reject_legacy_import_event_evidence_mutation_v1',
            'documents.legacy_import_variable_label_v1',
            'documents.legacy_import_normalize_template_variables_v1',
            'documents.legacy_import_canonical_json_v1',
            'documents.legacy_import_sha256_v1',
            'documents.legacy_import_timestamp_v1',
            'documents.legacy_import_exact_keys_v1',
            'documents.legacy_import_template_canonical_v1',
            'documents.legacy_import_envelope_canonical_v1',
            'documents.legacy_import_event_canonical_v1',
            'documents.legacy_import_target_canonical_v1',
            'documents.legacy_import_assert_membership_v1',
            'documents.legacy_import_assert_project_v1',
            'documents.legacy_import_assert_contact_v1',
            'documents.legacy_import_receipt_json_v1',
            'documents.begin_legacy_records_import_v1',
            'documents.quarantine_legacy_records_import_v1',
            'documents.apply_legacy_records_import_v1',
            'documents.verify_legacy_records_import_receipt_v1',
            'documents.rollback_legacy_records_import_v1',
        ],
        readsThrough: [
            'platform.membership-directory.v1',
            documentsTrainingRecordFactsProviderCapability,
            documentsKnowledgeSignedPolicyFactsCapability,
            documentsKnowledgeSignedPolicyAcknowledgementCapability,
            documentsWorkforceProtectedFormEligibilityCapability,
            documentsWorkforceProtectedFormSubmissionCapability,
            documentsEstimatingProposalAcceptanceFactsCapability,
            projectsProjectReferenceCapability,
            'platform.import-resolution.v1',
            'projects.import-resolution.v1',
            'relationships.import-resolution.v1',
        ],
        migrations: [
            'documents.template-foundation',
            'documents.instances-signatures-authenticity',
            'documents.portal-envelopes',
            'documents.estimating-acceptance-envelopes',
            'documents.training-record-artifacts',
            'documents.knowledge-signed-policy-artifacts',
            'documents.workforce-protected-employee-forms',
            'documents.legacy-records-import',
            'documents.legacy-history-recovery',
            'documents.legacy-import-membership-row-lock-acl',
            'documents.legacy-import-hash-schema-acl',
            'documents.legacy-import-template-constraint-resolution',
            'documents.legacy-import-nullable-resolution-semantics',
            'documents.legacy-import-duplicate-template-key-alias',
        ],
    },
    health: ['documents.database'],
    lifecycle: {
        initialization: { timeoutMs: 5_000, failureBehavior: 'quarantine-module' },
        resources: {
            requestConcurrency: 64,
            jobConcurrency: 0,
            queueBacklogLimit: 0,
            databaseConnectionBudget: 8,
            workerIsolation: 'shared-api',
        },
        disable: {
            data: 'retain',
            clientContributions: 'remove',
            serverRoutes: 'deny-tenant-access',
            scheduledJobs: 'skip',
            events: {
                behavior: 'drop-safe',
                durableCursor: false,
                retentionDays: 0,
                backlogLimit: 0,
                warningAt: 0,
                reconciliationProcedure: null,
                dropSafeReason: 'Documents publishes immutable template, lifecycle, signing, and delivery-intent facts but owns no provider or subscriber.',
            },
            outboundSideEffects: 'suppress-no-retroactive-send',
            inFlight: 'finish-idempotently',
        },
        resume: {
            configuration: 'last-compatible-published',
            healthChecks: ['documents.database'],
            reconciliationProcedure: null,
            visibility: 'after-health-and-reconciliation',
        },
        uninstall: {
            executableCode: 'remove',
            data: 'retain',
            tombstone: 'retain-immutable',
            destruction: 'separate-explicit-authority',
        },
    },
    retentionPolicy: 'Document templates, frozen instances, signatures, lifecycle facts, and command receipts remain stored while disabled.',
});
//# sourceMappingURL=documents.module.js.map